export function getChatLift(launcher, obstacles, minimumTop = 12, gap = 12) {
  const overlapping = obstacles.filter(
    (rect) => rect.width > 0 && rect.height > 0 && rect.left < launcher.right && rect.right > launcher.left
  );
  const clear = (top) => overlapping.every((rect) => top + launcher.height + gap <= rect.top || top >= rect.bottom + gap);
  if (clear(launcher.top)) return 0;
  const candidates = overlapping.map((rect) => rect.top - launcher.height - gap);
  const top = candidates.filter((value) => value >= minimumTop && value <= launcher.top && clear(value)).sort((a, b) => b - a)[0];
  return top === undefined ? 0 : launcher.top - top;
}

export function getCartChatTop(title, launcherHeight) {
  return title.top + (title.height - launcherHeight) / 2;
}

if (typeof document !== 'undefined') {
  const roots = new WeakSet();
  const resized = new WeakSet();
  let pending = false;
  let lift = 0;
  let currentHost;
  let currentLauncher;
  const schedule = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(update);
  };
  const mutations = new MutationObserver(schedule);
  const sizes = new ResizeObserver(schedule);
  const observeSize = (element) => {
    if (!resized.has(element)) {
      resized.add(element);
      sizes.observe(element);
    }
  };
  const findLauncher = (root) => {
    if (!roots.has(root)) {
      roots.add(root);
      mutations.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
      root.addEventListener('transitionend', schedule, true);
      root.addEventListener('animationend', schedule, true);
    }
    const launcher = root.querySelector('[part~="activator"]');
    if (launcher) return launcher;
    for (const element of root.querySelectorAll('*')) {
      if (element.shadowRoot) {
        const nested = findLauncher(element.shadowRoot);
        if (nested) return nested;
      }
    }
  };
  function update() {
    pending = false;
    const host = document.querySelector('shopify-chat');
    if (!host) return;
    if (host !== currentHost) {
      currentHost = host;
      lift = 0;
    }
    observeSize(host);
    const launcher = host.shadowRoot && findLauncher(host.shadowRoot);
    if (!launcher) return;
    if (launcher !== currentLauncher) {
      currentLauncher = launcher;
      launcher.addEventListener('focus', () => {
        if (!host.hasAttribute('data-armadyne-cart-anchor')) return;
        const title = document.querySelector('main .cart-title');
        if (title && (title.getBoundingClientRect().top < 80 || title.getBoundingClientRect().bottom > window.innerHeight)) {
          title.scrollIntoView({ block: 'center' });
        }
      });
    }
    observeSize(launcher);
    const cartTitle = document.querySelector('main .cart-title');
    const anchorToCart = cartTitle && window.matchMedia('(max-width: 749px)').matches;
    host.toggleAttribute('data-armadyne-cart-anchor', Boolean(anchorToCart));
    let nextLift = 0;
    if (host.getAttribute('mode') === 'standalone' && launcher.getAttribute('aria-expanded') !== 'true') {
      const rect = launcher.getBoundingClientRect();
      const natural = { left: rect.left, right: rect.right, top: rect.top + lift, height: rect.height };
      if (anchorToCart) {
        // Dense cart controls leave no reliable space for a floating mobile launcher.
        observeSize(cartTitle);
        const top = getCartChatTop(cartTitle.getBoundingClientRect(), rect.height);
        const headerBottom = Math.max(0, ...[...document.querySelectorAll('#header-group, header-component')].map((header) => header.getBoundingClientRect().bottom));
        nextLift = natural.top - top;
        host.style.setProperty('--armadyne-chat-cart-clip-top', `${Math.max(-12, headerBottom - top)}px`);
      } else {
        const controls = [...document.querySelectorAll('main variant-picker, main product-form-component, main sticky-add-to-cart .sticky-add-to-cart__bar[data-stuck="true"]')];
        const obstacles = controls.map((element) => {
          observeSize(element);
          return element.getBoundingClientRect();
        });
        const headers = [...document.querySelectorAll('#header-group, header-component')];
        const headerBottom = headers.reduce((bottom, header) => {
          observeSize(header);
          return Math.max(bottom, header.getBoundingClientRect().bottom);
        }, 0);
        const minimumTop = Math.max(12, headerBottom + 12, (window.visualViewport?.offsetTop ?? 0) + 12);
        if (rect.width && rect.height) {
          nextLift = getChatLift(natural, obstacles, minimumTop);
          if (nextLift > 0) {
            const text = [...document.querySelectorAll('main h1, main h2, main h3, main p, main product-price, main product-sku-component')];
            const textRects = text.map((element) => element.getBoundingClientRect());
            nextLift = getChatLift(natural, [...obstacles, ...textRects], minimumTop) || nextLift;
          }
        }
      }
    }
    if (Math.abs(nextLift - lift) > 0.5 || !host.style.getPropertyValue('--armadyne-chat-lift')) {
      lift = nextLift;
      host.style.setProperty('--armadyne-chat-lift', `${lift}px`);
    }
  }
  mutations.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['mode', 'aria-expanded', 'data-stuck', 'data-sticky-state', 'open', 'hidden'],
  });
  document.addEventListener('toggle', schedule, true);
  window.addEventListener('scroll', schedule, { passive: true, capture: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.visualViewport?.addEventListener('resize', schedule, { passive: true });
  window.visualViewport?.addEventListener('scroll', schedule, { passive: true });
  schedule();
}
